import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Accordion, Card, Spinner, Modal, Row, Col, FormGroup, FormLabel } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { isEmpty, cloneDeep } from 'lodash';
import Select, { components } from 'react-select';
import { MultiSelect } from 'react-multi-select-component';
import CustomTooltip from '../../Components/Tooltip/CustomTooltip';
import Loader from '../../Components/Utility/Loader/Loader';
import ConfirmationPopup from '../../Components/ModalPopup/ConfirmationPopup';
import * as action from './Store/SystemMasters.action';
import { useComponent } from '../UserSetting/UsersLibrary/UserRights';
import './SampleTypeGroupMaster.css';
import { toasterMsg } from '../../Components/Toaster/Toaster';

function StatusDot({ color }) {
  return (
    <span
      style={{
        display: 'inline-block',
        width: 18,
        height: 18,
        borderRadius: '50%',
        backgroundColor: color === 'amber' ? '#FFA500' : color,
        marginRight: 8,
        flexShrink: 0,
      }}
    />
  );
}

function StatusOption(props) {
  const { data } = props;

  return (
    <components.Option {...props}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <StatusDot color={data.statusColor} />
        <span>{data.label}</span>
      </div>
    </components.Option>
  );
}

function StatusSingleValue(props) {
  const { data } = props;

  return (
    <components.SingleValue {...props}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <StatusDot color={data.statusColor} />
        <span>{data.label}</span>
      </div>
    </components.SingleValue>
  );
}

const TEMP_SAMPLE_TYPE_GROUP = {
  sampleGroupName: '',
  brand: [],
  subBrand: [],
  licensed: 'no',
  licensor: [],
  license: [],
  likeness: [],
  productType: [],
  category1: [],
  category2: [],
  category3: [],
  category4: [],
  sampleTypeAutomation: [],
  isNew: true,
  isActive: true,
  _id: '',
};

/*
 * 'na' means licensing is not a criterion for the group: it matches licensed and non-licensed
 * products alike, and (like 'no') hides and clears licensor/license/likeness.
 */
const LICENSED_OPTIONS = [
  { value: 'no', labelKey: 'SAMPLE_TYPE_GROUP_MASTER.LICENSED_NO' },
  { value: 'yes', labelKey: 'SAMPLE_TYPE_GROUP_MASTER.LICENSED_YES' },
  { value: 'na', labelKey: 'SAMPLE_TYPE_GROUP_MASTER.LICENSED_NA' },
];

function SampleTypeGroupMaster() {
  const dispatch = useDispatch();
  const { t } = useTranslation(['sampleTypeGroupMaster', 'systemMaster']);

  const {
    SystemMastersDetails: {
      sampleTypeGroupList,
      brandList,
      subBrandList,
      licensorList,
      licenseList,
      likenessList,
      pcTypeList,
      category1List,
      category2List,
      category3List,
      category4List,
      sample_status,
    },
    UserDetails: { userInfo },
    Users: { logInRights },
  } = useSelector((state) => state);

  const { CheckActionPermission } = useComponent();
  const MasterAddEditAccess = CheckActionPermission(logInRights, 'masters', 'samples', 'addEdit');
  const MasterDeleteAccess = CheckActionPermission(logInRights, 'masters', 'samples', 'delete');

  const loggedInUserId = userInfo.userId;

  const [groupData, setGroupData] = useState([]);
  const [showGroups, setShowGroups] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteId, setDeleteId] = useState('');
  const [toggleDelete, setToggleDelete] = useState(false);
  const [toggleActiveInactive, setToggleActiveInactive] = useState(false);
  const [activeInactiveID, setActiveInactiveID] = useState('');
  const [showSidebar, setShowSidebar] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [sidebarLoading, setSidebarLoading] = useState(false);
  const [errors, setErrors] = useState({});

  /* Per-field product attribute ids the group's samples depend on — locked against removal */
  const [inUseAttributeValues, setInUseAttributeValues] = useState({});

  /* Modified states for adding multiple groups */
  const [newGroupInputs, setNewGroupInputs] = useState([]);
  const [isSavingGroups, setIsSavingGroups] = useState(false);
  const [editedGroupNames, setEditedGroupNames] = useState({});
  const [editedGroupErrors, setEditedGroupErrors] = useState({});

  /* Store automation drafts per sample type index */
  const [addingAutomationMap, setAddingAutomationMap] = useState({});

  /* State for dynamically fetched sub-brands */
  const [fetchedSubBrandList, setFetchedSubBrandList] = useState([]);

  /* States for category hierarchy tracking */
  const [fetchedCategory3List, setFetchedCategory3List] = useState([]);
  const [fetchedCategory4List, setFetchedCategory4List] = useState([]);

  /* States for cascading License and Likeness dropdowns */
  const [fetchedLicenseList, setFetchedLicenseList] = useState([]);
  const [fetchedLikenessListByLicense, setFetchedLikenessListByLicense] = useState([]);

  /* New state for new sample type inputs (draft sample types) */
  const [newSampleTypeInputs, setNewSampleTypeInputs] = useState([]);
  /* Track sample type name errors for new inputs */
  const [sampleTypeErrors, setSampleTypeErrors] = useState({});

  const updateSampleTypeNameEverywhere = (group, sampleTypeId, newName) => {
    const updated = cloneDeep(group);

    updated.sampleTypeAutomation.forEach((st) => {
      if (st.sampleType._id === sampleTypeId) {
        st.sampleType.name = newName;
      }

      st.configuration?.forEach((config) => {
        if (config.changeTo?._id === sampleTypeId) {
          config.changeTo.name = newName;
        }
      });
    });

    return updated;
  };

  useEffect(() => {
    setGroupData(sampleTypeGroupList || []);
  }, [sampleTypeGroupList]);

  const fetchGroups = async () => {
    console.log('🚀 ~ SampleTypeGroupMaster.js:174 ~ fetchGroups ~ showGroups:', showGroups);
    if (!showGroups) {
      setIsLoading(true);
      setShowGroups(true);
      await dispatch(action.getSampleTypeGroupDetails({ loggedInUserId }));
      setIsLoading(false);
    } else {
      setShowGroups(false);
    }
  };

  const fetchDropdownData = useCallback(async () => {
    setSidebarLoading(true);
    await Promise.all([
      isEmpty(brandList) && dispatch(action.getBrandDetails({ loggedInUserId })),
      isEmpty(pcTypeList) && dispatch(action.getPCTypesDetails({ includeInactive: true }, true)),
      isEmpty(category1List) && dispatch(action.getCategory1Details({ loggedInUserId, categoryType: 1 })),
      isEmpty(category2List) && dispatch(action.getCategory2Details({ loggedInUserId, categoryType: 2 })),
      isEmpty(category3List) && dispatch(action.getCategory3Details({ loggedInUserId, categoryType: 3 })),
      isEmpty(category4List) && dispatch(action.getCategory4Details({ loggedInUserId, categoryType: 4 })),
      isEmpty(licensorList) && dispatch(action.getLicensorDetails({ loggedInUserId })),
      isEmpty(licenseList) && dispatch(action.getLicenseDetails({ loggedInUserId })),
      isEmpty(likenessList) && dispatch(action.getLikenessDetails({ loggedInUserId })),
      isEmpty(sample_status) && dispatch(action.getSampleStatusDetails({ loggedInUserId })),
    ]);
    setSidebarLoading(false);
  }, [
    dispatch,
    loggedInUserId,
    brandList,
    pcTypeList,
    category1List,
    category2List,
    category3List,
    category4List,
    licensorList,
    licenseList,
    likenessList,
    sample_status,
  ]);

  const extractId = (item) => {
    if (typeof item === 'string') return item;
    if (item && typeof item === 'object') return item._id || item.value || item;
    return item;
  };

  const normalizeGroupIds = (group) => {
    const normalizedGroup = cloneDeep(group);
    ['brand', 'subBrand', 'licensor', 'license', 'likeness', 'productType',
     'category1', 'category2', 'category3', 'category4'].forEach((field) => {
      if (normalizedGroup[field]) normalizedGroup[field] = normalizedGroup[field].map(extractId);
    });
    return normalizedGroup;
  };

  const openSidebar = async (group) => {
    const clonedGroup = normalizeGroupIds(group);

    setSelectedGroup(clonedGroup);
    setShowSidebar(true);
    setErrors({});
    setAddingAutomationMap({});
    setNewSampleTypeInputs([]); /* Clear new sample type inputs when opening sidebar */
    setSampleTypeErrors({}); /* Clear sample type errors */
    setInUseAttributeValues({});

    /* Fetch dropdown data */
    await fetchDropdownData();

    if (clonedGroup?._id) {
      try {
        const attributeValues = await dispatch(
          action.getSampleTypeGroupInUseAttributeValues({ sampleTypeGroupId: String(clonedGroup._id) }),
        );
        setInUseAttributeValues((attributeValues?.data ?? attributeValues) || {});
      } catch (error) {
        console.error('Error resolving in-use attribute values:', error);
      }
    }

    /* If group has existing brands, fetch their sub-brands */
    if (clonedGroup?.brand && clonedGroup.brand.length > 0) {
      const brandIds = clonedGroup.brand.map(extractId);
      const subBrandRequests = brandIds.map((brandId) => dispatch(action.getSubBrandDetails({ loggedInUserId, brandId })));
      const results = await Promise.all(subBrandRequests);
      const allSubBrands = results.flatMap((result) => (Array.isArray(result) ? result : []));
      setFetchedSubBrandList(allSubBrands);
    }

    /* If group has existing licensor, fetch licenses for those licensors */
    if (clonedGroup?.licensor && clonedGroup.licensor.length > 0) {
      const licensorIds = clonedGroup.licensor.map(extractId);
      const licenseRequests = licensorIds.map((licensorId) => dispatch(action.getLicenseDetails({ loggedInUserId, licensorId })));
      const results = await Promise.all(licenseRequests);
      const allLicenses = results.flatMap((result) => (Array.isArray(result) ? result : []));
      setFetchedLicenseList(allLicenses);
    }

    /* If group has existing license, fetch likeness for those licenses and licensors */
    if (clonedGroup?.license && clonedGroup.license.length > 0 && clonedGroup?.licensor && clonedGroup.licensor.length > 0) {
      const licensorIds = clonedGroup.licensor.map(extractId);
      const licenseIds = clonedGroup.license.map(extractId);
      const likenessRequests = licensorIds.flatMap((licensorId) =>
        licenseIds.map((licenseId) => dispatch(action.getLikenessDetails({ loggedInUserId, licensorId, licenseId }))),
      );
      const results = await Promise.all(likenessRequests);
      const allLikeness = results.flatMap((result) => (Array.isArray(result) ? result : []));
      setFetchedLikenessListByLicense(allLikeness);
    }

    /* If group has existing category hierarchy, fetch the respective categories */
    if (clonedGroup?.category1?.length > 0 && clonedGroup?.category2?.length > 0) {
      const category1Ids = clonedGroup.category1.map(extractId);
      const category2Ids = clonedGroup.category2.map(extractId);
      const results = await dispatch(
        action.getCategory3Details({
          loggedInUserId,
          categoryType: 3,
          isActive: true,
          categoryOneId: category1Ids,
          categoryTwoId: category2Ids,
        }),
      );
      const allCategory3 = Array.isArray(results) ? results : [];
      setFetchedCategory3List(allCategory3);
    }

    /* If group has category 3, fetch category 4 */
    if (clonedGroup?.category1?.length > 0 && clonedGroup?.category2?.length > 0 && clonedGroup?.category3?.length > 0) {
      const category1Ids = clonedGroup.category1.map(extractId);
      const category2Ids = clonedGroup.category2.map(extractId);
      const category3Ids = clonedGroup.category3.map(extractId);
      const results = await dispatch(
        action.getCategory4Details({
          loggedInUserId,
          categoryType: 4,
          isActive: true,
          categoryOneId: category1Ids,
          categoryTwoId: category2Ids,
          categoryThreeId: category3Ids,
        }),
      );
      const allCategory4 = Array.isArray(results) ? results : [];
      setFetchedCategory4List(allCategory4);
    }
  };

  const closeSidebar = () => {
    setShowSidebar(false);
    /* Reset cascading dropdown states */
    setFetchedLicenseList([]);
    setFetchedLikenessListByLicense([]);
    setNewSampleTypeInputs([]);
    setSampleTypeErrors({});
    setInUseAttributeValues({});
  };

  /* Add a new input field */
  const addNewGroupInput = async () => {
    if (!showGroups) {
      await fetchGroups();
    }

    /* Add a new empty input with a unique ID */
    setNewGroupInputs((prev) => [
      ...prev,
      {
        id: Date.now(), /* Unique ID for each input */
        value: '',
        error: null,
      },
    ]);
  };

  /* Update a specific input value */
  const updateGroupInput = (id, value) => {
    setNewGroupInputs((prev) => prev.map((input) => (input.id === id ? { ...input, value, error: value.trim() ? null : input.error } : input)));
  };

  /* Remove a specific input */
  const removeGroupInput = (id) => {
    setNewGroupInputs((prev) => prev.filter((input) => input.id !== id));
  };

  /* Validate all inputs */
  const validateGroupInputs = () => {
    const existingGroups = groupData.map((g) => g.sampleGroupName.toLowerCase());
    let hasErrors = false;

    const updatedInputs = newGroupInputs.map((input) => {
      const value = input.value.trim();

      if (!value) {
        hasErrors = true;
        return { ...input, error: t('SAMPLE_TYPE_GROUP_MASTER.GROUP_NAME_REQUIRED') };
      }

      if (existingGroups.includes(value.toLowerCase())) {
        hasErrors = true;
        return { ...input, error: t('SAMPLE_TYPE_GROUP_MASTER.GROUP_NAME_EXISTS') };
      }

      if (value.length > 50) {
        hasErrors = true;
        return { ...input, error: t('SAMPLE_TYPE_GROUP_MASTER.GROUP_NAME_TOO_LONG') };
      }

      /* Check for duplicates within new inputs */
      const duplicateCount = newGroupInputs.filter((inp) => inp.value.trim().toLowerCase() === value.toLowerCase() && inp.id !== input.id).length;

      if (duplicateCount > 0) {
        hasErrors = true;
        return { ...input, error: t('SAMPLE_TYPE_GROUP_MASTER.DUPLICATE_NAME') };
      }

      return { ...input, error: null };
    });

    setNewGroupInputs(updatedInputs);
    return !hasErrors;
  };

  const validateEditedGroupNames = () => {
    const errors = {};
    const allGroupNames = groupData.map((group) => ({
      id: group._id,
      name: (editedGroupNames[group._id] ?? group.sampleGroupName).toLowerCase(),
    }));
    const newInputNames = newGroupInputs.map((i) => i.value.trim().toLowerCase());

    for (const [groupId, newName] of Object.entries(editedGroupNames)) {
      const trimmed = newName.trim();
      if (!trimmed) {
        errors[groupId] = t('SAMPLE_TYPE_GROUP_MASTER.GROUP_NAME_REQUIRED');
        continue;
      }
      if (trimmed.length > 50) {
        errors[groupId] = t('SAMPLE_TYPE_GROUP_MASTER.GROUP_NAME_TOO_LONG');
        continue;
      }
      const isDupe =
        allGroupNames.some((group) => group.id !== groupId && group.name === trimmed.toLowerCase()) ||
        newInputNames.includes(trimmed.toLowerCase());
      if (isDupe) {
        errors[groupId] = t('SAMPLE_TYPE_GROUP_MASTER.GROUP_NAME_EXISTS');
      }
    }
    setEditedGroupErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /* Save all group names at once */
  const saveAllGroupNames = async () => {
    const hasEdits = Object.keys(editedGroupNames).length > 0;
    const hasNew = newGroupInputs.length > 0;
    if (!hasEdits && !hasNew) return;

    const editsValid = hasEdits ? validateEditedGroupNames() : true;
    const newValid = hasNew ? validateGroupInputs() : true;
    if (!editsValid || !newValid) return;

    setIsSavingGroups(true);
    try {
      for (const [groupId, newName] of Object.entries(editedGroupNames)) {
        const group = groupData.find((g) => g._id === groupId);
        if (!group) continue;
        const payload = {
          loggedInUserId,
          sampleTypeGroup: { ...normalizeGroupIds(group), sampleGroupName: newName.trim() },
        };
        await dispatch(action.saveSampleTypeGroupToSystemMaster(payload, t));
      }

      for (const input of newGroupInputs) {
        const groupName = input.value.trim();
        if (groupName) {
          const payload = {
            loggedInUserId,
            sampleTypeGroup: { ...TEMP_SAMPLE_TYPE_GROUP, sampleGroupName: groupName },
          };
          await dispatch(action.saveSampleTypeGroupToSystemMaster(payload, t));
        }
      }

      await dispatch(action.getSampleTypeGroupDetails({ loggedInUserId }));
      setEditedGroupNames({});
      setEditedGroupErrors({});
      setNewGroupInputs([]);
      toasterMsg(t('SAMPLE_TYPE_GROUP_MASTER.SAVED_SUCCESSFULLY'), 'success');
    } catch (error) {
      toasterMsg(t('SAMPLE_TYPE_GROUP_MASTER.SAVE_ERROR'), 'danger');
    } finally {
      setIsSavingGroups(false);
    }
  };

  /* Cancel adding all groups */
  const cancelAddingAllGroups = () => {
    setNewGroupInputs([]);
  };

  /* Handle Enter key press for individual inputs */
  const handleInputKeyPress = (e, id) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      /* Find the index of current input */
      const currentIndex = newGroupInputs.findIndex((input) => input.id === id);
      /* If it's the last input, add a new one */
      if (currentIndex === newGroupInputs.length - 1) {
        addNewGroupInput();
      } else {
        /* Otherwise focus on next input */
        const nextInput = document.getElementById(`group-input-${newGroupInputs[currentIndex + 1].id}`);
        if (nextInput) nextInput.focus();
      }
    } else if (e.key === 'Escape' && e.ctrlKey) {
      cancelAddingAllGroups();
    }
  };

  const handleInlineGroupNameChange = (groupId, newName) => {
    const original = groupData.find((g) => g._id === groupId)?.sampleGroupName ?? '';
    setEditedGroupNames((prev) => {
      if (newName === original) {
        const { [groupId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [groupId]: newName };
    });
    setEditedGroupErrors((prev) => {
      const { [groupId]: _, ...rest } = prev;
      return rest;
    });
  };

  const brandOptions = useMemo(() => {
    return brandList?.filter((b) => b.isActive)?.map((b) => ({ value: b._id, label: b.name })) || [];
  }, [brandList]);

  const subBrandOptions = useMemo(() => {
    if (!selectedGroup?.brand?.length) return [];
    return fetchedSubBrandList?.filter((sb) => sb.isActive)?.map((sb) => ({ value: sb._id, label: sb.name })) || [];
  }, [fetchedSubBrandList]);

  const licensorOptions = useMemo(() => {
    const baseOptions = licensorList?.filter((l) => l.isActive)?.map((l) => ({ value: l._id, label: l.name })) || [];

    /* If selectedGroup has licensor values not in baseOptions, add them */
    if (selectedGroup?.licensor?.length) {
      const licensorMap = new Map(baseOptions.map((o) => [o.value, o]));
      selectedGroup.licensor.forEach((licensorId) => {
        if (!licensorMap.has(licensorId)) {
          const licensorItem = licensorList?.find((l) => l._id === licensorId);
          if (licensorItem) {
            licensorMap.set(licensorId, { value: licensorId, label: licensorItem.name });
          }
        }
      });
      return Array.from(licensorMap.values());
    }

    return baseOptions;
  }, [licensorList, selectedGroup?.licensor]);

  const licenseOptions = useMemo(() => {
    /* Use fetched license list if available (from licensor selection), otherwise use the Redux list */
    const sourceList = fetchedLicenseList.length > 0 ? fetchedLicenseList : licenseList;
    const baseOptions = sourceList?.filter((l) => l.isActive)?.map((l) => ({ value: l._id, label: l.name })) || [];

    /* If selectedGroup has license values not in baseOptions, add them */
    if (selectedGroup?.license?.length) {
      const licenseMap = new Map(baseOptions.map((o) => [o.value, o]));
      selectedGroup.license.forEach((licenseId) => {
        if (!licenseMap.has(licenseId)) {
          const licenseItem = sourceList?.find((l) => l._id === licenseId);
          if (licenseItem) {
            licenseMap.set(licenseId, { value: licenseId, label: licenseItem.name });
          }
        }
      });
      return Array.from(licenseMap.values());
    }

    return baseOptions;
  }, [licenseList, fetchedLicenseList, selectedGroup?.license]);

  const likenessOptions = useMemo(() => {
    /* Use fetched likeness list if available (from license selection), otherwise use the Redux list */
    const sourceList = fetchedLikenessListByLicense.length > 0 ? fetchedLikenessListByLicense : likenessList;
    const baseOptions = sourceList?.filter((l) => l.isActive)?.map((l) => ({ value: l._id, label: l.name })) || [];

    /* If selectedGroup has likeness values not in baseOptions, add them */
    if (selectedGroup?.likeness?.length) {
      const likenessMap = new Map(baseOptions.map((o) => [o.value, o]));
      selectedGroup.likeness.forEach((likenessId) => {
        if (!likenessMap.has(likenessId)) {
          const likenessItem = sourceList?.find((l) => l._id === likenessId);
          if (likenessItem) {
            likenessMap.set(likenessId, { value: likenessId, label: likenessItem.name });
          }
        }
      });
      return Array.from(likenessMap.values());
    }

    return baseOptions;
  }, [likenessList, fetchedLikenessListByLicense, selectedGroup?.likeness]);

  /**
   * Disable exactly the option values that the group's existing samples depend on, so an
   * in-use scope cannot be narrowed while every other value stays selectable.
   * @param {Array<{value: string, label: string}>} options - Options rendered by the field
   * @param {string} fieldName - Group field these options belong to (e.g. 'brand', 'category1')
   * @returns {Array<{value: string, label: string, disabled?: boolean}>} Options with in-use ones locked
   */
  const applyInUseLock = (options, fieldName) => {
    const lockedIds = inUseAttributeValues?.[fieldName];
    if (!lockedIds?.length) return options;
    const lockedIdSet = lockedIds.map((lockedId) => String(lockedId));
    return options.map((option) => (lockedIdSet.includes(String(option.value)) ? { ...option, disabled: true } : option));
  };

  const productTypeOptions = useMemo(() => {
    return pcTypeList?.filter((c) => c.isActive)?.map((c) => ({ value: c._id, label: c.name })) || [];
  }, [pcTypeList]);

  const category1Options = useMemo(() => {
    return category1List?.filter((c) => c.isActive && c.type === 1)?.map((c) => ({ value: c._id, label: c.name })) || [];
  }, [category1List]);

  const category2Options = useMemo(() => {
    return category2List?.filter((c) => c.isActive && c.type === 2)?.map((c) => ({ value: c._id, label: c.name })) || [];
  }, [category2List]);

  const category3Options = useMemo(() => {
    return category3List?.filter((c) => c.isActive && c.type === 3)?.map((c) => ({ value: c._id, label: c.name })) || [];
  }, [category3List]);

  const category4Options = useMemo(() => {
    return category4List?.filter((c) => c.isActive && c.type === 4)?.map((c) => ({ value: c._id, label: c.name })) || [];
  }, [category4List]);

  const sampleStatusOptions = useMemo(() => {
    return sample_status?.filter((s) => s.isActive)?.map((s) => ({ value: s._id, label: s.name, statusColor: s.statusColor })) || [];
  }, [sample_status]);

  /* Combine existing sample types and new sample type inputs for automation dropdowns */
  const sampleTypeOptionsForAutomation = useMemo(() => {
    const existingTypes =
      selectedGroup?.sampleTypeAutomation?.map((st) => ({
        value: st.sampleType._id,
        label: st.sampleType.name,
      })) || [];

    const newTypes = newSampleTypeInputs
      .filter((input) => input.value.trim()) /* Only include non-empty inputs */
      .map((input, index) => ({
        value: `new_${input.id}`,
        label: input.value.trim(),
        isNew: true,
      }));

    return [...existingTypes, ...newTypes];
  }, [selectedGroup?.sampleTypeAutomation, newSampleTypeInputs]);

  const handleGroupNameChange = (value) => {
    setSelectedGroup((prev) => ({ ...prev, sampleGroupName: value }));
    if (errors.sampleGroupName) {
      setErrors((prev) => ({ ...prev, sampleGroupName: null }));
    }
  };

  const handleMultiSelectChange = async (field, values) => {
    const ids = values?.map((v) => v.value) || [];

    if (field === 'brand') {
      setSelectedGroup((prev) => ({ ...prev, [field]: ids, subBrand: [] }));

      if (ids.length > 0) {
        const subBrandRequests = ids.map((brandId) => dispatch(action.getSubBrandDetails({ loggedInUserId, brandId })));
        const results = await Promise.all(subBrandRequests);

        const allSubBrands = results.flatMap((result) => (Array.isArray(result) ? result : []));
        setFetchedSubBrandList(allSubBrands);
      } else {
        setFetchedSubBrandList([]);
      }
    } else if (field === 'licensor') {
      setSelectedGroup((prev) => ({ ...prev, [field]: ids, license: [], likeness: [] }));
      setFetchedLicenseList([]);
      setFetchedLikenessListByLicense([]);

      if (ids.length > 0) {
        const licenseRequests = ids.map((licensorId) => dispatch(action.getLicenseDetails({ loggedInUserId, licensorId })));
        const results = await Promise.all(licenseRequests);
        const allLicenses = results.flatMap((result) => (Array.isArray(result) ? result : []));
        setFetchedLicenseList(allLicenses);
      } else {
        setFetchedLicenseList([]);
      }
    } else if (field === 'license') {
      setSelectedGroup((prev) => ({ ...prev, [field]: ids, likeness: [] }));
      setFetchedLikenessListByLicense([]);

      if (ids.length > 0 && selectedGroup?.licensor?.length > 0) {
        try {
          const likenessRequests = selectedGroup.licensor.flatMap((licensorId) =>
            ids.map((licenseId) => dispatch(action.getLikenessDetails({ loggedInUserId, licensorId, licenseId }))),
          );
          const results = await Promise.all(likenessRequests);

          const allLikeness = results.flatMap((result) => (Array.isArray(result) ? result : []));
          setFetchedLikenessListByLicense(allLikeness);
          console.log('Fetched likeness:', allLikeness);
        } catch (error) {
          console.error('Error fetching likeness:', error);
        }
      } else {
        setFetchedLikenessListByLicense([]);
      }
    } else if (field === 'category1') {
      setSelectedGroup((prev) => ({ ...prev, [field]: ids, category2: [], category3: [], category4: [] }));
      setFetchedCategory3List([]);
      setFetchedCategory4List([]);
    } else if (field === 'category2') {
      setSelectedGroup((prev) => ({ ...prev, [field]: ids, category3: [], category4: [] }));
      setFetchedCategory3List([]);
      setFetchedCategory4List([]);

      if (ids.length > 0 && selectedGroup?.category1?.length > 0) {
        const results = await dispatch(
          action.getCategory3Details({
            loggedInUserId,
            categoryType: 3,
            isActive: true,
            categoryOneId: selectedGroup.category1,
            categoryTwoId: ids,
          }),
        );
        const allCategory3 = Array.isArray(results) ? results : [];
        setFetchedCategory3List(allCategory3);
      }
    } else if (field === 'category3') {
      setSelectedGroup((prev) => ({ ...prev, [field]: ids, category4: [] }));

      if (ids.length > 0 && selectedGroup?.category1?.length > 0 && selectedGroup?.category2?.length > 0) {
        const results = await dispatch(
          action.getCategory4Details({
            loggedInUserId,
            categoryType: 4,
            isActive: true,
            categoryOneId: selectedGroup.category1,
            categoryTwoId: selectedGroup.category2,
            categoryThreeId: ids,
          }),
        );
        const allCategory4 = Array.isArray(results) ? results : [];
        setFetchedCategory4List(allCategory4);
      }
    } else {
      setSelectedGroup((prev) => ({ ...prev, [field]: ids }));
    }

    /* Clear validation errors on field change */
    if (field === 'category1') {
      /* category1 cascade resets category2 to [] — clear both errors to avoid stale messages */
      if (errors.category1 || errors.category2) {
        setErrors((prev) => ({ ...prev, category1: null, category2: null }));
      }
    } else if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleLicensedChange = (option) => {
    /* 'na' ignores licensing, so it clears the licensor/license/likeness selection just like 'no' */
    const clearsLicensing = option.value === 'no' || option.value === 'na';
    setSelectedGroup((prev) => ({
      ...prev,
      licensed: option.value,
      ...(clearsLicensing && { licensor: [], license: [], likeness: [] }),
    }));
    /* Reset fetched lists when toggling licensed status */
    if (clearsLicensing) {
      setFetchedLicenseList([]);
      setFetchedLikenessListByLicense([]);
    }
  };

  /* Add a new sample type input field */
  const addNewSampleTypeInput = () => {
    const newId = Date.now();
    setNewSampleTypeInputs((prev) => [
      ...prev,
      {
        id: newId,
        value: '',
      },
    ]);
  };

  /* Update a sample type input value */
  const updateSampleTypeInput = (id, value) => {
    setNewSampleTypeInputs((prev) => prev.map((input) => (input.id === id ? { ...input, value } : input)));

    /* Clear error for this input if it exists */
    if (sampleTypeErrors[id]) {
      setSampleTypeErrors((prev) => {
        const updated = { ...prev };
        delete updated[id];
        return updated;
      });
    }
  };

  /* Remove a sample type input */
  const removeSampleTypeInput = (id) => {
    setNewSampleTypeInputs((prev) => prev.filter((input) => input.id !== id));

    /* Clear error for this input if it exists */
    if (sampleTypeErrors[id]) {
      setSampleTypeErrors((prev) => {
        const updated = { ...prev };
        delete updated[id];
        return updated;
      });
    }
  };

  /* Validate new sample type inputs */
  const validateNewSampleTypes = () => {
    const newErrors = {};
    let hasErrors = false;

    /* Get all existing sample type names (from saved sample types) */
    const existingSampleTypeNames = selectedGroup?.sampleTypeAutomation?.map((st) => st.sampleType.name.toLowerCase()) || [];

    /* Check each new sample type input */
    newSampleTypeInputs.forEach((input) => {
      const value = input.value.trim();

      if (!value) {
        newErrors[input.id] = t('SAMPLE_TYPE_GROUP_MASTER.SAMPLE_TYPE_NAME_REQUIRED');
        hasErrors = true;
      } else if (value.length > 50) {
        newErrors[input.id] = t('SAMPLE_TYPE_GROUP_MASTER.SAMPLE_TYPE_NAME_TOO_LONG');
        hasErrors = true;
      } else if (existingSampleTypeNames.includes(value.toLowerCase())) {
        newErrors[input.id] = t('SAMPLE_TYPE_GROUP_MASTER.SAMPLE_TYPE_EXISTS');
        hasErrors = true;
      } else {
        /* Check for duplicates within new inputs */
        const duplicateCount = newSampleTypeInputs.filter((inp) => inp.id !== input.id && inp.value.trim().toLowerCase() === value.toLowerCase()).length;

        if (duplicateCount > 0) {
          newErrors[input.id] = t('SAMPLE_TYPE_GROUP_MASTER.DUPLICATE_SAMPLE_TYPE');
          hasErrors = true;
        }
      }
    });

    setSampleTypeErrors(newErrors);
    return !hasErrors;
  };

  /* Convert new sample type inputs to sampleTypeAutomation format */
  const convertNewSampleTypesToAutomation = () => {
    return newSampleTypeInputs
      .filter((input) => input.value.trim()) /* Only include non-empty inputs */
      .map((input) => ({
        sampleType: {
          _id: `new_${input.id}`, /* Temporary ID for new sample types */
          name: input.value.trim(),
        },
        configuration: [],
      }));
  };

  const isSampleTypeUsedByOthers = (sampleTypeId) => {
    return selectedGroup?.sampleTypeAutomation?.some((st) => st.configuration?.some((config) => config.changeTo?._id === sampleTypeId));
  };

  const removeSampleType = async (index) => {
    const sampleTypeId = selectedGroup.sampleTypeAutomation[index].sampleType._id;
    const sampleTypeIdStr = sampleTypeId?.toString?.() ?? sampleTypeId;

    const isUsedInConfig = isSampleTypeUsedByOthers(sampleTypeId);
    if (isUsedInConfig) {
      toasterMsg(t('SAMPLE_TYPE_GROUP_MASTER.SAMPLE_TYPE_IN_USE'), 'danger');
      return;
    }

    try {
      const result = await dispatch(action.checkSampleTypeInUse({ sampleTypeId: sampleTypeIdStr }));
      if (result?.inUse && result?.messageKey) {
        toasterMsg(t(result.messageKey), 'danger');
        return;
      }
    } catch (err) {
      toasterMsg(t('SAMPLE_TYPE_GROUP_MASTER.SAMPLE_TYPE_NAME_IN_USE') || err?.message, 'danger');
      return;
    }

    setSelectedGroup((prev) => ({
      ...prev,
      sampleTypeAutomation: prev.sampleTypeAutomation.filter((_, i) => i !== index),
    }));

    setAddingAutomationMap((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  };

  const getAutomationCountForSampleType = (sampleTypeIndex) => {
    const savedCount = selectedGroup?.sampleTypeAutomation?.[sampleTypeIndex]?.configuration?.length || 0;
    const draftCount = addingAutomationMap?.[sampleTypeIndex]?.configs?.length || 0;
    return savedCount + draftCount;
  };

  /* Start adding automation for a sample type */
  const startAddingAutomation = (sampleTypeIndex) => {
    const maxAllowed = sampleStatusOptions.length; /* based on sample status count */
    const currentCount = getAutomationCountForSampleType(sampleTypeIndex);

    if (currentCount >= maxAllowed) {
      toasterMsg(t('SAMPLE_TYPE_GROUP_MASTER.AUTOMATION_LIMIT_REACHED') || `You can add only ${maxAllowed} automation rules for this sample type`, 'warning');
      return;
    }

    setAddingAutomationMap((prev) => {
      const updated = { ...prev };
      if (!updated[sampleTypeIndex]) {
        updated[sampleTypeIndex] = { configs: [] };
      }
      updated[sampleTypeIndex].configs.push({ onAction: null, changeTo: null });
      return updated;
    });
  };

  /* Handle changes in draft automations */
  const handleDraftAutomationChange = (sampleTypeIndex, configIndex, field, option) => {
    setAddingAutomationMap((prev) => {
      const updated = { ...prev };
      if (!updated[sampleTypeIndex]) {
        updated[sampleTypeIndex] = { configs: [] };
      }

      if (!updated[sampleTypeIndex].configs[configIndex]) {
        updated[sampleTypeIndex].configs[configIndex] = { onAction: null, changeTo: null };
      }

      updated[sampleTypeIndex].configs[configIndex][field] = option;
      return updated;
    });
  };

  /* Remove draft automation */
  const removeDraftAutomation = (sampleTypeIndex, configIndex) => {
    setAddingAutomationMap((prev) => {
      const updated = { ...prev };
      if (updated[sampleTypeIndex]?.configs) {
        updated[sampleTypeIndex].configs.splice(configIndex, 1);

        /* If no more draft configs, remove the entry */
        if (updated[sampleTypeIndex].configs.length === 0) {
          delete updated[sampleTypeIndex];
        }
      }
      return updated;
    });
  };

  /* Handle changes in saved automations */
  const handleAutomationChange = (sampleTypeIndex, automationIndex, field, option) => {
    const updated = cloneDeep(selectedGroup);
    if (field === 'onAction') {
      updated.sampleTypeAutomation[sampleTypeIndex].configuration[automationIndex].onAction = {
        _id: option.value,
        statusName: option.label,
      };
    } else {
      updated.sampleTypeAutomation[sampleTypeIndex].configuration[automationIndex].changeTo = {
        _id: option.value,
        name: option.label,
      };
    }
    setSelectedGroup(updated);
  };

  const removeAutomation = async (sampleTypeIndex, automationIndex) => {
    const config = selectedGroup?.sampleTypeAutomation?.[sampleTypeIndex]?.configuration?.[automationIndex];
    const fromId = selectedGroup?.sampleTypeAutomation?.[sampleTypeIndex]?.sampleType?._id;
    const changeToId = config?.changeTo?._id;
    const groupId = selectedGroup?._id;
    if (groupId && fromId && changeToId) {
      try {
        const result = await dispatch(
          action.checkAutomationConfigInUse({
            sampleTypeGroupId: groupId.toString?.() ?? groupId,
            fromSampleTypeId: fromId.toString?.() ?? fromId,
            changeToSampleTypeId: changeToId.toString?.() ?? changeToId,
          }),
        );
        const data = result?.data ?? result;
        if (data?.inUse && data?.messageKey) {
          toasterMsg(t(data.messageKey), 'danger');
          return;
        }
      } catch (err) {
        toasterMsg(t('SAMPLE_TYPE_GROUP_MASTER.AUTOMATION_IN_USE_CANNOT_REMOVE'), 'danger');
        return;
      }
    }
    const updated = cloneDeep(selectedGroup);
    updated.sampleTypeAutomation[sampleTypeIndex].configuration.splice(automationIndex, 1);
    setSelectedGroup(updated);
  };

  /* Merge new sample types and automations into the main data structure */
  const normalizeAutomationDrafts = (group) => {
    const updated = cloneDeep(group);

    /* Convert new sample type inputs to sampleTypeAutomation format */
    const newSampleTypes = convertNewSampleTypesToAutomation();

    /* Combine existing sample types with new ones */
    updated.sampleTypeAutomation = [...(updated.sampleTypeAutomation || []), ...newSampleTypes];

    /* Add draft automations to their respective sample types */
    Object.entries(addingAutomationMap).forEach(([index, draft]) => {
      const stIndex = parseInt(index);
      if (draft?.configs && updated.sampleTypeAutomation[stIndex]) {
        draft.configs.forEach((config) => {
          if (config?.onAction && config?.changeTo) {
            /* Check for duplicates in existing configurations */
            const isDuplicate = updated.sampleTypeAutomation[stIndex].configuration.some(
              (existing) => existing.onAction._id === config.onAction.value && existing.changeTo._id === config.changeTo.value,
            );

            if (!isDuplicate) {
              updated.sampleTypeAutomation[stIndex].configuration.push({
                onAction: {
                  _id: config.onAction.value,
                  statusName: config.onAction.label,
                },
                changeTo: {
                  _id: config.changeTo.value,
                  name: config.changeTo.label,
                },
              });
            }
          }
        });
      }
    });

    return updated;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!selectedGroup?.sampleGroupName?.trim()) {
      newErrors.sampleGroupName = t('SAMPLE_TYPE_GROUP_MASTER.GROUP_NAME_REQUIRED');
    }
    if (!selectedGroup?.brand?.length) {
      newErrors.brand = t('SAMPLE_TYPE_GROUP_MASTER.BRAND_REQUIRED');
    }
    if (!selectedGroup?.productType?.length) {
      newErrors.productType = t('SAMPLE_TYPE_GROUP_MASTER.PRODUCT_TYPE_REQUIRED');
    }
    if (!selectedGroup?.category1?.length) {
      newErrors.category1 = t('SAMPLE_TYPE_GROUP_MASTER.CATEGORY_1_REQUIRED');
    }
    if (!selectedGroup?.category2?.length) {
      newErrors.category2 = t('SAMPLE_TYPE_GROUP_MASTER.CATEGORY_2_REQUIRED');
    }

    const names = selectedGroup?.sampleTypeAutomation?.map((st) => (st.sampleType?.name ?? '').toLowerCase().trim()) ?? [];
    const duplicateName = names.some((name, i) => name && names.indexOf(name) !== i);
    if (duplicateName) {
      newErrors.sampleTypeNames = t('SAMPLE_TYPE_GROUP_MASTER.SAMPLE_TYPE_EXISTS');
    }

    setErrors(newErrors);

    /* Also validate new sample types */
    const sampleTypesValid = validateNewSampleTypes();

    return Object.keys(newErrors).length === 0 && sampleTypesValid;
  };

  const saveGroup = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        loggedInUserId,
        sampleTypeGroup: normalizeAutomationDrafts(selectedGroup),
      };

      await dispatch(action.saveSampleTypeGroupToSystemMaster(payload, t));

      /* Refresh the group data to get updated IDs for new sample types */
      const updatedGroups = await dispatch(action.getSampleTypeGroupDetails({ loggedInUserId }));

      /* Find the updated group to keep the sidebar open with fresh data */
      const updatedGroup =
        updatedGroups?.find((g) => g._id === selectedGroup._id) || updatedGroups?.find((g) => g.sampleGroupName === selectedGroup.sampleGroupName);

      if (updatedGroup) {
        /* Re-open sidebar with updated group to properly load all cascading dropdowns and data */
        await openSidebar(updatedGroup);
      }
      setIsSaving(false);
      toasterMsg(t('SAMPLE_TYPE_GROUP_MASTER.SAVED_SUCCESSFULLY'), 'success');
      setNewSampleTypeInputs([]);
      setAddingAutomationMap({});
      setSampleTypeErrors({});
    } catch (error) {
      console.log('Error saving sample type group:', error);
      toasterMsg(t('SAMPLE_TYPE_GROUP_MASTER.SAVE_ERROR'), 'danger');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setToggleDelete(true);
  };

  const confirmDelete = async () => {
    setIsLoading(true);
    await dispatch(action.deleteSampleTypeGroupFromSystemMasters({ loggedInUserId, sampleTypeGroupId: deleteId }, t));
    await dispatch(action.getSampleTypeGroupDetails({ loggedInUserId }));
    setEditedGroupNames((prev) => {
      const { [deleteId]: _, ...rest } = prev;
      return rest;
    });
    setEditedGroupErrors((prev) => {
      const { [deleteId]: _, ...rest } = prev;
      return rest;
    });
    setIsLoading(false);
    setToggleDelete(false);
  };

  const handleActiveInactive = (id) => {
    setActiveInactiveID(id);
    setToggleActiveInactive(true);
  };

  const confirmToggleActive = async () => {
    const group = groupData.find((g) => g._id === activeInactiveID);
    if (group) {
      await dispatch(
        action.setSampleTypeGroupActiveInactive(
          {
            sampleTypeGroupId: group._id,
            isActive: !group.isActive,
            loggedInUserId,
          },
          t,
        ),
      );
      await dispatch(action.getSampleTypeGroupDetails({ loggedInUserId }));
      setToggleActiveInactive(false);
    }
  };

  const getSelectedValues = (ids, options) => {
    if (!ids?.length) return [];
    return options.filter((opt) => ids.includes(opt.value));
  };

  const handleSampleTypeNameChange = (stIndex, value) => {
    const sampleTypeId = selectedGroup?.sampleTypeAutomation?.[stIndex]?.sampleType?._id;
    if (!sampleTypeId) return;
    const updatedGroup = updateSampleTypeNameEverywhere(selectedGroup, sampleTypeId, value);
    setSelectedGroup(updatedGroup);
    if (errors.sampleTypeNames) {
      setErrors((prev) => ({ ...prev, sampleTypeNames: null }));
    }
  };

  /* Handle Enter key for sample type inputs */
  const handleSampleTypeInputKeyPress = (e, id) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      /* Find the index of current input */
      const currentIndex = newSampleTypeInputs.findIndex((input) => input.id === id);
      /* If it's the last input, add a new one */
      if (currentIndex === newSampleTypeInputs.length - 1) {
        addNewSampleTypeInput();
      } else {
        /* Otherwise focus on next input */
        const nextInput = document.getElementById(`sample-type-input-${newSampleTypeInputs[currentIndex + 1].id}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  return (
    <>
      <Accordion className="mb-4 master-accordian sample-type-group-master">
        <Card>
          <Card.Header>
            <div className="title-container">
              <p>{t('SAMPLE_TYPE_GROUP_MASTER.TITLE')}</p>
            </div>
            <div className="d-flex align-items-center gap-2">
              {MasterAddEditAccess && (
                <CustomTooltip tooltipText={t('SAMPLE_TYPE_GROUP_MASTER.ADD_GROUP')}>
                  <button
                    className="btn btn-outline sm ml-auto mr-2 -0"
                    onClick={addNewGroupInput}
                    aria-label={t('SAMPLE_TYPE_GROUP_MASTER.ADD_GROUP')}
                    disabled={isSavingGroups}
                    type="button"
                  >
                    <span className="icon-Add f-20" />
                  </button>
                </CustomTooltip>
              )}
              <CustomTooltip tooltipText={t(showGroups ? 'CUSTOM_ATTRIBUTES.CLOSE' : 'CUSTOM_ATTRIBUTES.OPEN')}>
                <button
                  className="btn d-inline-flex p-0"
                  onClick={fetchGroups}
                  aria-label={t(showGroups ? 'CUSTOM_ATTRIBUTES.CLOSE' : 'CUSTOM_ATTRIBUTES.OPEN')}
                  type="button"
                >
                  <i className={`icon-new-down-arrow-icon ${showGroups ? 'icon-inverse' : ''} f-24`} />
                </button>
              </CustomTooltip>
            </div>
          </Card.Header>
          {showGroups && (
            <Card.Body>
              {isLoading ? (
                <Loader />
              ) : (
                <>
                  <div className="common-scrollbar flex-fill">
                    <ul>
                      {/* Existing groups list */}
                      {groupData.map((group) => (
                        <li key={group._id}>
                          <div className="input-container flex-grow-1">
                            {MasterAddEditAccess ? (
                              <>
                                <input
                                  type="text"
                                  className={`px-0${!group.isActive ? ' text-muted' : ''}`}
                                  value={editedGroupNames[group._id] ?? group.sampleGroupName}
                                  onChange={(e) => handleInlineGroupNameChange(group._id, e.target.value)}
                                  maxLength={50}
                                  disabled={isSavingGroups}
                                />
                                {editedGroupErrors[group._id] && (
                                  <div className="invalid-feedback d-block">{editedGroupErrors[group._id]}</div>
                                )}
                              </>
                            ) : (
                              <span className={!group.isActive ? 'text-muted' : ''}>{group.sampleGroupName}</span>
                            )}
                          </div>
                          <div className="input-action d-flex">
                            {MasterAddEditAccess && (
                              <CustomTooltip tooltipText={t('SAMPLE_TYPE_GROUP_MASTER.CONFIGURE')}>
                                <button
                                  type="button"
                                  className="btn p-0 custom-action mr-0"
                                  onClick={() => openSidebar(group)}
                                  aria-label={t('SAMPLE_TYPE_GROUP_MASTER.CONFIGURE')}
                                >
                                  <span className="icon-settings f-24" />
                                </button>
                              </CustomTooltip>
                            )}
                            {MasterDeleteAccess && (
                              <CustomTooltip tooltipText={t('CUSTOM_ATTRIBUTES.DELETE')}>
                                <button type="button" className="btn custom-action mr-0" onClick={() => handleDelete(group._id)} aria-label="delete">
                                  <span className="icon-delete f-24" />
                                </button>
                              </CustomTooltip>
                            )}
                          </div>
                        </li>
                      ))}

                      {/* Dynamic inputs for adding new groups - Appear at the end */}
                      {newGroupInputs.map((input, index) => (
                        <li key={input.id} className="adding-new-group">
                          <div className="input-container flex-grow-1">
                            <input
                              id={`group-input-${input.id}`}
                              type="text"
                              className="form-control px-0"
                              value={input.value}
                              onChange={(e) => updateGroupInput(input.id, e.target.value)}
                              onKeyDown={(e) => handleInputKeyPress(e, input.id)}
                              placeholder={t('SAMPLE_TYPE_GROUP_MASTER.ENTER_GROUP_NAME')}
                              maxLength={50}
                              autoFocus={index === newGroupInputs.length - 1}
                            />
                            {input.error && <div className="invalid-feedback d-block">{input.error}</div>}
                          </div>
                          <div className="input-action d-flex">
                            <CustomTooltip tooltipText={t('SAMPLE_TYPE_GROUP_MASTER.REMOVE')}>
                              <button
                                type="button"
                                className="btn p-0 custom-action mr-0"
                                onClick={() => removeGroupInput(input.id)}
                                disabled={isSavingGroups}
                                aria-label={t('SAMPLE_TYPE_GROUP_MASTER.REMOVE')}
                              >
                                <span className="icon-delete f-24" />
                              </button>
                            </CustomTooltip>
                          </div>
                        </li>
                      ))}

                      {groupData.length === 0 && newGroupInputs.length === 0 && (
                        <li className="text-center text-muted">{t('SAMPLE_TYPE_GROUP_MASTER.NO_GROUPS')}</li>
                      )}
                    </ul>
                  </div>
                  {(newGroupInputs.length > 0 || Object.keys(editedGroupNames).length > 0) && (
                    <div className="d-flex justify-content-end gap-2 mt-3">
                      <CustomTooltip tooltipText={t('common:COMMON_LABEL.SAVE')}>
                        <button aria-label="Save" type="button" className="btn btn-outline-none " onClick={saveAllGroupNames} disabled={isSavingGroups}>
                          {isSavingGroups ? <Spinner animation="border" size="sm" className="mr-2" /> : <span className="icon-Tick-1 f-26" />}
                        </button>
                      </CustomTooltip>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          )}
        </Card>
      </Accordion>

      {/* Sidebar Panel - Configuration Modal */}
      <Modal className="sample-type-group-sidebar common-custom-modal right" show={showSidebar} onHide={closeSidebar}>
        <Modal.Header>
          <button aria-label="Save" type="button" className="btn btn-save-header" onClick={saveGroup} disabled={isSaving}>
            {isSaving ? <Spinner animation="border" size="sm" /> : t('SAMPLE_TYPE_GROUP_MASTER.SAVE')}
          </button>
          <div className="header-center">
            <p className="f-24 text-bold mb-0">{selectedGroup?.sampleGroupName || ''}</p>
          </div>
          <span onClick={closeSidebar} className="icon-close cursor-pointer close-icon-header" />
        </Modal.Header>

        <Modal.Body className="p-0">
          {sidebarLoading ? (
            <div className="p-4">
              <Loader />
            </div>
          ) : (
            <div className="sidebar-content common-scrollbar" style={{ maxHeight: 'calc(100vh - 50px)' }}>
              {/* Brand & Sub Brand Row */}

              <Col className="p-0">
                <div className="common-react-select">
                  <p className="field-name">
                    {t('SAMPLE_TYPE_GROUP_MASTER.BRAND')}
                    <span className="asterisk">*</span>
                  </p>
                  <MultiSelect
                    className="common-new-multiselect w-100"
                    options={applyInUseLock(brandOptions, 'brand')}
                    value={getSelectedValues(selectedGroup?.brand, brandOptions)}
                    onChange={(selected) => handleMultiSelectChange('brand', selected)}
                    labelledBy={t('SAMPLE_TYPE_GROUP_MASTER.SELECT_BRAND')}
                    overrideStrings={{ selectSomeItems: t('SAMPLE_TYPE_GROUP_MASTER.SELECT_BRAND') }}
                  />
                  <span className="error-message d-block">{errors.brand}</span>
                </div>
              </Col>
              <Col className="p-0">
                <div className="common-react-select">
                  <p className="field-name">{t('SAMPLE_TYPE_GROUP_MASTER.SUB_BRAND')}</p>
                  <MultiSelect
                    options={applyInUseLock(subBrandOptions, 'subBrand')}
                    className="common-new-multiselect w-100"
                    value={getSelectedValues(selectedGroup?.subBrand, subBrandOptions)}
                    onChange={(selected) => handleMultiSelectChange('subBrand', selected)}
                    labelledBy={t('SAMPLE_TYPE_GROUP_MASTER.SELECT_SUB_BRAND')}
                    overrideStrings={{ selectSomeItems: t('SAMPLE_TYPE_GROUP_MASTER.SELECT_SUB_BRAND') }}
                    disabled={!selectedGroup?.brand?.length}
                  />
                </div>
              </Col>

              {/* Licensed */}
              <FormGroup className="mb-2 w-100 d-flex gap-2">
                <FormLabel className="text-book">{t('SAMPLE_TYPE_GROUP_MASTER.LICENSED')}</FormLabel>
                <div className="d-flex ">
                  {LICENSED_OPTIONS.map((option) => (
                    <div key={option.value} className="custom-radio ml-2">
                      <input
                        className="form-check-input pr-1"
                        type="radio"
                        id={`licensed-${option.value}`}
                        name="licensed"
                        value={option.value}
                        checked={selectedGroup?.licensed === option.value}
                        onChange={() => handleLicensedChange(option)}
                      />
                      <label className="form-check-label" htmlFor={`licensed-${option.value}`}>
                        {t(option.labelKey)}
                      </label>
                    </div>
                  ))}
                </div>
              </FormGroup>

              {/* Conditional Licensor/License/Likeness */}
              {selectedGroup?.licensed === 'yes' && (
                <Row>
                  <Col md={4} className="pl-3 pr-0">
                    <div className="common-react-select">
                      <p className="field-name">{t('SAMPLE_TYPE_GROUP_MASTER.LICENSOR')}</p>
                      <MultiSelect
                        className="common-new-multiselect w-100"
                        options={applyInUseLock(licensorOptions, 'licensor')}
                        value={getSelectedValues(selectedGroup?.licensor, licensorOptions)}
                        onChange={(selected) => handleMultiSelectChange('licensor', selected)}
                        labelledBy={t('SAMPLE_TYPE_GROUP_MASTER.SELECT_LICENSOR')}
                        overrideStrings={{ selectSomeItems: t('SAMPLE_TYPE_GROUP_MASTER.SELECT_LICENSOR') }}
                      />
                    </div>
                  </Col>
                  <Col md={4} className="pl-3 pr-0">
                    <div className="common-react-select">
                      <p className="field-name">{t('SAMPLE_TYPE_GROUP_MASTER.LICENSE')}</p>
                      <MultiSelect
                        className="common-new-multiselect w-100"
                        options={applyInUseLock(licenseOptions, 'license')}
                        value={getSelectedValues(selectedGroup?.license, licenseOptions)}
                        onChange={(selected) => handleMultiSelectChange('license', selected)}
                        labelledBy={t('SAMPLE_TYPE_GROUP_MASTER.SELECT_LICENSE')}
                        overrideStrings={{ selectSomeItems: t('SAMPLE_TYPE_GROUP_MASTER.SELECT_LICENSE') }}
                      />
                    </div>
                  </Col>
                  <Col md={4} className="pl-3 pr-0">
                    <div className="common-react-select">
                      <p className="field-name">{t('SAMPLE_TYPE_GROUP_MASTER.LIKENESS')}</p>
                      <MultiSelect
                        className="common-new-multiselect w-100"
                        options={applyInUseLock(likenessOptions, 'likeness')}
                        value={getSelectedValues(selectedGroup?.likeness, likenessOptions)}
                        onChange={(selected) => handleMultiSelectChange('likeness', selected)}
                        labelledBy={t('SAMPLE_TYPE_GROUP_MASTER.SELECT_LIKENESS')}
                        overrideStrings={{ selectSomeItems: t('SAMPLE_TYPE_GROUP_MASTER.SELECT_LIKENESS') }}
                      />
                    </div>
                  </Col>
                </Row>
              )}

              {/* Product Type */}
              <Col className="p-0">
                <div className="common-react-select">
                  <p className="field-name">
                    {t('SAMPLE_TYPE_GROUP_MASTER.PRODUCT_TYPE')}
                    <span className="asterisk">*</span>
                  </p>
                  <MultiSelect
                    className="common-new-multiselect w-100"
                    options={applyInUseLock(productTypeOptions, 'productType')}
                    value={getSelectedValues(selectedGroup?.productType, productTypeOptions)}
                    onChange={(selected) => handleMultiSelectChange('productType', selected)}
                    labelledBy={t('SAMPLE_TYPE_GROUP_MASTER.SELECT_PRODUCT_TYPE')}
                    overrideStrings={{ selectSomeItems: t('SAMPLE_TYPE_GROUP_MASTER.SELECT_PRODUCT_TYPE') }}
                  />
                  <span className="error-message d-block">{errors.productType}</span>
                </div>
              </Col>

              {/* Categories Row */}
              <Col className="p-0">
                <div className="common-react-select">
                  <p className="field-name">
                    {t('SAMPLE_TYPE_GROUP_MASTER.CATEGORY_1')}
                    <span className="asterisk">*</span>
                  </p>
                  <MultiSelect
                    className="common-new-multiselect w-100"
                    options={applyInUseLock(category1Options, 'category1')}
                    value={getSelectedValues(selectedGroup?.category1, category1Options)}
                    onChange={(selected) => handleMultiSelectChange('category1', selected)}
                    labelledBy={t('SAMPLE_TYPE_GROUP_MASTER.SELECT_CATEGORY')}
                    overrideStrings={{ selectSomeItems: t('SAMPLE_TYPE_GROUP_MASTER.SELECT_CATEGORY') }}
                  />
                  <span className="error-message d-block">{errors.category1}</span>
                </div>
              </Col>
              <Col className="p-0">
                <div className="common-react-select">
                  <p className="field-name">
                    {t('SAMPLE_TYPE_GROUP_MASTER.CATEGORY_2')}
                    <span className="asterisk">*</span>
                  </p>
                  <MultiSelect
                    className="common-new-multiselect w-100"
                    options={applyInUseLock(category2Options, 'category2')}
                    value={getSelectedValues(selectedGroup?.category2, category2Options)}
                    onChange={(selected) => handleMultiSelectChange('category2', selected)}
                    labelledBy={t('SAMPLE_TYPE_GROUP_MASTER.SELECT_CATEGORY')}
                    overrideStrings={{ selectSomeItems: t('SAMPLE_TYPE_GROUP_MASTER.SELECT_CATEGORY') }}
                  />
                  <span className="error-message d-block">{errors.category2}</span>
                </div>
              </Col>
              <Col className="p-0">
                <div className="common-react-select">
                  <p className="field-name">{t('SAMPLE_TYPE_GROUP_MASTER.CATEGORY_3')}</p>
                  <MultiSelect
                    className="common-new-multiselect w-100"
                    options={applyInUseLock(category3Options, 'category3')}
                    value={getSelectedValues(selectedGroup?.category3, category3Options)}
                    onChange={(selected) => handleMultiSelectChange('category3', selected)}
                    labelledBy={t('SAMPLE_TYPE_GROUP_MASTER.SELECT_CATEGORY')}
                    overrideStrings={{ selectSomeItems: t('SAMPLE_TYPE_GROUP_MASTER.SELECT_CATEGORY') }}
                  />
                </div>
              </Col>
              <Col className="p-0">
                <div className="common-react-select">
                  <p className="field-name">{t('SAMPLE_TYPE_GROUP_MASTER.CATEGORY_4')}</p>
                  <MultiSelect
                    className="common-new-multiselect w-100"
                    options={applyInUseLock(category4Options, 'category4')}
                    value={getSelectedValues(selectedGroup?.category4, category4Options)}
                    onChange={(selected) => handleMultiSelectChange('category4', selected)}
                    labelledBy={t('SAMPLE_TYPE_GROUP_MASTER.SELECT_CATEGORY')}
                    overrideStrings={{ selectSomeItems: t('SAMPLE_TYPE_GROUP_MASTER.SELECT_CATEGORY') }}
                  />
                </div>
              </Col>

              {/* Sample Types Section */}
              <div className="sample-types-section">
                {errors.sampleTypeNames && <div className="text-danger small mb-2">{errors.sampleTypeNames}</div>}
                {/* Saved Sample Types List */}
                {selectedGroup?.sampleTypeAutomation?.map((sampleType, stIndex) => (
                  <div key={sampleType.sampleType._id || stIndex} className="sample-type-item">
                    <div className="d-flex w-full justify-content-between align-items-center">
                      <input
                        type="text"
                        className="form-control"
                        value={sampleType.sampleType.name ?? ''}
                        maxLength={50}
                        onChange={(e) => handleSampleTypeNameChange(stIndex, e.target.value)}
                      />
                      <div className="sample-type-actions ml-2 d-flex">
                        <button type="button" className="btn p-0 d-flex" onClick={() => removeSampleType(stIndex)}>
                          <span className="icon-delete f-20" />
                        </button>
                      </div>
                    </div>

                    {/* Automation Configurations */}
                    <div className="card-body py-1 px-0">
                      {/* Display ALL configurations - both existing and draft ones */}
                      {[...(sampleType.configuration || []), ...(addingAutomationMap[stIndex]?.configs || [])].map((config, configIndex) => {
                        const isNewConfig = configIndex >= (sampleType.configuration?.length || 0);
                        const actualConfigIndex = isNewConfig ? configIndex - (sampleType.configuration?.length || 0) : configIndex;

                        return (
                          <div key={`${stIndex}-${configIndex}`} className="d-flex align-items-center mb-2 ">
                            <div className="d-flex align-items-center">
                              <span className="fw-semibold mr-1">{t('SAMPLE_TYPE_GROUP_MASTER.ON')}</span>
                              <div className="common-react-select style-all-border mb-0 w-150px">
                                {isNewConfig ? (
                                  <Select
                                    options={sampleStatusOptions}
                                    value={addingAutomationMap[stIndex]?.configs[actualConfigIndex]?.onAction || null}
                                    onChange={(opt) => handleDraftAutomationChange(stIndex, actualConfigIndex, 'onAction', opt)}
                                    placeholder={t('SAMPLE_TYPE_GROUP_MASTER.SELECT_STATUS')}
                                    isClearable
                                    className="common-select"
                                    classNamePrefix="common-select"
                                    components={{
                                      Option: StatusOption,
                                      SingleValue: StatusSingleValue,
                                    }}
                                    maxMenuHeight={140}
                                    menuPosition="fixed"
                                  />
                                ) : (
                                  <Select
                                    options={sampleStatusOptions}
                                    value={sampleStatusOptions.find((o) => o.value === config.onAction?._id) || null}
                                    onChange={(opt) => handleAutomationChange(stIndex, configIndex, 'onAction', opt)}
                                    placeholder={t('SAMPLE_TYPE_GROUP_MASTER.SELECT_STATUS')}
                                    className="common-select"
                                    classNamePrefix="common-select"
                                    components={{
                                      Option: StatusOption,
                                      SingleValue: StatusSingleValue,
                                    }}
                                    maxMenuHeight={140}
                                    menuPosition="fixed"
                                  />
                                )}
                              </div>
                            </div>
                            <div className="d-flex align-items-center gap-1 w-100 justify-content-around ml-2">
                              <span className="fw-semibold mr-1">{t('SAMPLE_TYPE_GROUP_MASTER.CHANGE')}</span>
                              <div className="common-react-select style-all-border mb-0 w-150px mr-1 !outline-none">
                                {isNewConfig ? (
                                  <Select
                                    options={sampleTypeOptionsForAutomation}
                                    value={addingAutomationMap[stIndex]?.configs[actualConfigIndex]?.changeTo || null}
                                    onChange={(opt) => handleDraftAutomationChange(stIndex, actualConfigIndex, 'changeTo', opt)}
                                    placeholder={t('SAMPLE_TYPE_GROUP_MASTER.SELECT_SAMPLE_TYPE')}
                                    isClearable
                                    className="common-select"
                                    classNamePrefix="common-select"
                                    maxMenuHeight={140}
                                    menuPosition="fixed"
                                  />
                                ) : (
                                  <Select
                                    options={sampleTypeOptionsForAutomation}
                                    value={sampleTypeOptionsForAutomation.find((o) => o.value === config.changeTo?._id) || null}
                                    onChange={(opt) => handleAutomationChange(stIndex, configIndex, 'changeTo', opt)}
                                    placeholder={t('SAMPLE_TYPE_GROUP_MASTER.SELECT_SAMPLE_TYPE')}
                                    className="common-select"
                                    classNamePrefix="common-select"
                                    maxMenuHeight={140}
                                    menuPosition="fixed"
                                  />
                                )}
                              </div>
                            </div>
                            <button
                              aria-label="remove"
                              type="button"
                              className="btn p-0 d-flex"
                              onClick={() => (isNewConfig ? removeDraftAutomation(stIndex, actualConfigIndex) : removeAutomation(stIndex, configIndex))}
                            >
                              <span className="icon-delete f-16" style={{ fontSize: '18px' }} />
                            </button>
                          </div>
                        );
                      })}

                      {/* Add Automation Button */}
                      <div className="text-right d-flex justify-content-end align-items-center gap-1 mt-2">
                        <span className="font-medium">{t('SAMPLE_TYPE_GROUP_MASTER.ADD_AUTOMATION')}</span>
                        <button aria-label="add automation" type="button" className="btn btn-outline sm  mr-0" onClick={() => startAddingAutomation(stIndex)}>
                          <span className="icon-Add f-16" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                {/* New Sample Type Inputs */}
                {newSampleTypeInputs.map((input, index) => (
                  <div key={input.id} className="sample-type-item">
                    <div className="d-flex w-full justify-content-between align-items-center">
                      <div className="input-container flex-grow-1">
                        <input
                          id={`sample-type-input-${input.id}`}
                          type="text"
                          className="form-control"
                          value={input.value}
                          onChange={(e) => updateSampleTypeInput(input.id, e.target.value)}
                          onKeyDown={(e) => handleSampleTypeInputKeyPress(e, input.id)}
                          placeholder={t('SAMPLE_TYPE_GROUP_MASTER.ENTER_SAMPLE_TYPE_NAME')}
                          maxLength={50}
                          autoFocus={index === newSampleTypeInputs.length - 1}
                        />
                        {sampleTypeErrors[input.id] && <div className="invalid-feedback d-block">{sampleTypeErrors[input.id]}</div>}
                      </div>
                      <div className="sample-type-actions ml-2 d-flex">
                        <CustomTooltip tooltipText={t('SAMPLE_TYPE_GROUP_MASTER.REMOVE')}>
                          <button
                            type="button"
                            className="btn p-0 d-flex mr-0"
                            onClick={() => removeSampleTypeInput(input.id)}
                            aria-label={t('SAMPLE_TYPE_GROUP_MASTER.REMOVE')}
                          >
                            <span className="icon-delete f-20" />
                          </button>
                        </CustomTooltip>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Add Sample Type Button */}
                <div className="d-flex align-items-center mt-3">
                  <span>{t('SAMPLE_TYPE_GROUP_MASTER.ADD_SAMPLE_TYPE')}</span>
                  <button type="button" className="btn btn-outline sm mr-2 -0" onClick={addNewSampleTypeInput}>
                    <span className="icon-Add f-16" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Confirmation Popups */}
      {toggleDelete && (
        <ConfirmationPopup
          onOpen={toggleDelete}
          onConfirm={confirmDelete}
          title={t('SAMPLE_TYPE_GROUP_MASTER.DELETE_CONFIRMATION.TITLE')}
          message={t('SAMPLE_TYPE_GROUP_MASTER.DELETE_CONFIRMATION.MESSAGE')}
          toggle={() => setToggleDelete(false)}
          loading={isLoading}
        />
      )}
      {toggleActiveInactive && (
        <ConfirmationPopup
          onOpen={toggleActiveInactive}
          onConfirm={confirmToggleActive}
          title={t('SAMPLE_TYPE_GROUP_MASTER.ACTIVE_INACTIVE_CONFIRMATION.TITLE')}
          message={t('SAMPLE_TYPE_GROUP_MASTER.ACTIVE_INACTIVE_CONFIRMATION.MESSAGE')}
          toggle={() => setToggleActiveInactive(false)}
          loading={isLoading}
        />
      )}
    </>
  );
}

SampleTypeGroupMaster.propTypes = {};

export default SampleTypeGroupMaster;